import { PlusOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { useCanForView } from '@salescore/client-common'
import { selectCurrentCachedView, useAppSelector } from '@salescore/client-redux'
import { isSharedLinkPresetName } from '@salescore/core'
import { ButtonWithTooltip, NotionLikeSelector, removeQueryParameterFromUrl } from '@salescore/frontend-common'
import { App, Button } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactNode, useEffect } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import { useIsSavingSettings } from '../../../../hooks/useIsSavingSettings'
import { sheetPickedPresetNameAtom } from '../../../../recoil/navigation/atoms'
import { changesAtom } from '../../../../recoil/recordsHistory'
import {
  isSavingWithoutInteractionAtom,
  type SalescoreObjectAbility,
  type ViewAbility,
} from '../../../../recoil/view/atoms'
import { configAtom } from '../../../../recoil/viewConfig'
import { buildSheetPresetFormModalProperties } from './SheetPresetFormModal'

export const SheetPresetsPicker = ({
  view,
  ability,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactNode => {
  const { message, modal } = App.useApp()
  const [config, setConfig] = useRecoilState(configAtom)
  const [, setIsSavingSettings] = useIsSavingSettings()
  const setIsSavingWithoutInteraction = useSetRecoilState(isSavingWithoutInteractionAtom)
  const [picked, setPicked] = useRecoilState(sheetPickedPresetNameAtom)
  const currentCachedView = useAppSelector((state) => selectCurrentCachedView(state, { viewId: view.id }))
  const canUpdate = useCanForView('update', view)
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)

  const changes = useRecoilValue(changesAtom)

  function select(presetName: string): void {
    // プリセットが[共有リンク]以外になった際に URL から shareId を除去 (reload での再注入を防ぐ)。
    // redux の shareId は残す — dispatch すると useSharedUrl (Suspense query) が再発火し画面がちらつくため
    if (!isSharedLinkPresetName(presetName)) {
      removeQueryParameterFromUrl('shareId')
    }
    setConfig((previous) => {
      if (previous.type !== 'sheet' || previous.presets === undefined) {
        return previous
      }

      const pickedPreset = previous.presets.find((x) => x.name === presetName)
      return {
        ...previous,
        tree: pickedPreset?.tree ?? previous.tree,
        fields: pickedPreset?.fields ?? previous.fields,
        // 以下propertyは未設定の場合undefinedになりうるためprevで上書きしない
        filterTree: pickedPreset?.filterTree,
        sorters: pickedPreset?.sorters,
        meta: pickedPreset?.meta,
        // プリセットを選択した時点で共有リンクプリセットを消した方が良いかもしれない
        // presets: previous.presets.filter(x => !isSharedLinkPresetName(x.name)),
      }
    })
    setPicked(presetName)
    setIsSavingWithoutInteraction(true)
  }

  useEffect(() => {
    // プリセットを選択しているキャッシュが存在する場合は何もしない
    // sheetPickedPresetNameへの値のセット自体はinitializeStateで行なっている
    if (currentCachedView?.sheetPickedPresetName !== undefined) {
      return
    }

    // 初期表示時は先頭のプリセットをデフォルト設定とする
    if (picked === undefined && config.type === 'sheet' && config.presets?.[0] !== undefined) {
      select(config.presets[0].name)
    }
  }, [])

  if (config.type !== 'sheet') {
    return null
  }

  if (config.presets === undefined || config.presets.length === 0) {
    return (
      <ButtonWithTooltip
        type="text"
        disabled={!canUpdate || !canDisplayConfigEdit}
        showTooltip={!canUpdate || !canDisplayConfigEdit}
        tooltipTitle={t(`権限がありません`)}
        icon={<PlusOutlined />}
        onClick={() => {
          const m = modal.confirm(
            buildSheetPresetFormModalProperties({
              preset: undefined,
              onAfterFinish: () => {
                m.destroy()
              },
              ability,
            }),
          )
        }}
      >
        {t(`プリセットの新規作成`)}
      </ButtonWithTooltip>
    )
  }

  return (
    <>
      <NotionLikeSelector
        value={picked ?? config.presets[0]?.name}
        options={config.presets.map((x) => ({ value: x.name, label: x.name, disableDrag: false }))}
        placeholder={t(`プリセット`)}
        additionText={t(`プリセットの新規作成`)}
        uneditable={isSharedLinkPresetName}
        onAddButtonClick={() => {
          const m = modal.confirm(
            buildSheetPresetFormModalProperties({
              preset: undefined,
              onAfterFinish: () => {
                m.destroy()
              },
              ability,
            }),
          )
        }}
        setValue={(value) => {
          if (changes.length > 0 || hasChangeToViewSchema) {
            const messageKey = 'select-preset-warning'
            message.warning({
              key: messageKey,
              content: (
                <span>
                  {t(`変更差分がある場合はプリセットの変更により差分が削除される可能性があります。`)}
                  <br />
                  {t(`プリセットを変更しますか？`)}
                  <Button
                    className="ml-2"
                    onClick={() => {
                      select(value)
                      message.destroy(messageKey)
                    }}
                  >
                    {t(`はい`)}
                  </Button>
                </span>
              ),
              duration: 3,
            })
          } else {
            select(value)
          }
        }}
        onSort={
          canUpdate && canDisplayConfigEdit
            ? (values: string[]) => {
                const sortedPresets = values
                  .map((value) => config.presets?.find((x) => x.name === value))
                  .filter(isNotNullish)
                setConfig({
                  ...config,
                  presets: sortedPresets,
                })
                // ダッシュボードと仕様を合わせるため「設定を保存」ボタン押下をskipし並び替えた直後に保存する
                setIsSavingSettings(true)
              }
            : undefined
        }
        onEdit={
          canUpdate && canDisplayConfigEdit
            ? (value) => {
                const preset = config.presets?.find((x) => x.name === value)
                const m = modal.confirm(
                  buildSheetPresetFormModalProperties({
                    preset,
                    onAfterFinish: () => {
                      m.destroy()
                    },
                    ability,
                  }),
                )
              }
            : undefined
        }
        onDestroy={
          canUpdate && canDisplayConfigEdit
            ? (value: string): void => {
                // [共有リンク]を削除する場合は shareId を URL から取り除く
                if (isSharedLinkPresetName(value)) {
                  removeQueryParameterFromUrl('shareId')
                }
                setConfig((previous) => {
                  if (previous.type !== 'sheet' || previous.presets === undefined) {
                    return previous
                  }
                  const newPresets = previous.presets.filter((p) => p.name !== value)
                  const firstPreset = newPresets[0]
                  if ((picked !== undefined && picked !== value) || firstPreset === undefined) {
                    return {
                      ...previous,
                      presets: newPresets,
                    }
                  }
                  // 削除対象が選択中のプリセットの場合は先頭のプリセットを選択する
                  return {
                    ...previous,
                    tree: firstPreset.tree ?? previous.tree,
                    fields: firstPreset.fields ?? previous.fields,
                    // 以下propertyは未設定の場合undefinedになりうるためprevで上書きしない
                    filterTree: firstPreset.filterTree,
                    sorters: firstPreset.sorters,
                    meta: firstPreset.meta,
                    presets: newPresets,
                  }
                })
                if (picked === value) {
                  setPicked(undefined)
                }
                setHasChangeToViewSchema(true)
              }
            : undefined
        }
        disableAddition={!canUpdate || !canDisplayConfigEdit}
        showAddition={true}
        className="mr-2"
        buttonProps={{ type: 'text' }}
      />
    </>
  )
}
